import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { BEFORE_AFTER_PAIRS } from "../../lib/config";

/* Single draggable before/after comparison viewport. */
const CompareViewport = ({ before, after, label, index }) => {
  const containerRef = useRef(null);
  const [pct, setPct] = useState(52);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.min(96, Math.max(4, raw));
    setPct(clamped);
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const up = () => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  const startDrag = (e) => {
    draggingRef.current = true;
    document.body.style.userSelect = "none";
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(x);
    e.preventDefault && e.preventDefault();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPct((p) => Math.max(4, p - 3));
    if (e.key === "ArrowRight") setPct((p) => Math.min(96, p + 3));
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden select-none"
      style={{ aspectRatio: "16 / 9", maxHeight: "78vh", cursor: "ew-resize" }}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      data-testid={`before-after-viewport-${index}`}
    >
      {/* AFTER (base) */}
      <img
        src={after}
        alt={`After — ${label}`}
        loading={index === 0 ? "eager" : "lazy"}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* BEFORE (clipped from the right) */}
      <img
        src={before}
        alt={`Before — ${label}`}
        loading={index === 0 ? "eager" : "lazy"}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
      />

      {/* Corner pills */}
      <div
        className="absolute top-4 left-4 md:top-6 md:left-6 pointer-events-none"
        style={{
          background: "rgba(18,43,32,0.7)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          border: "1px solid rgba(198,161,91,0.55)",
          color: "#C6A15B",
          padding: "6px 12px",
          borderRadius: "9999px",
          fontSize: "0.65rem",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        Before
      </div>
      <div
        className="absolute top-4 right-4 md:top-6 md:right-6 pointer-events-none"
        style={{
          background: "rgba(27,67,50,0.85)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          border: "1px solid rgba(198,161,91,0.55)",
          color: "#FAF7F2",
          padding: "6px 12px",
          borderRadius: "9999px",
          fontSize: "0.65rem",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        After
      </div>

      {/* Location label */}
      {label && (
        <div
          className="absolute bottom-4 left-4 md:bottom-6 md:left-6 pointer-events-none"
          style={{
            background: "rgba(250,247,242,0.14)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(198,161,91,0.4)",
            color: "#FAF7F2",
            padding: "8px 14px",
            borderRadius: "9999px",
            fontSize: "0.72rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontWeight: 500,
          }}
        >
          {label}
        </div>
      )}

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{
          left: `${pct}%`,
          transform: "translateX(-50%)",
          width: 2,
          background:
            "linear-gradient(180deg, rgba(198,161,91,0.85) 0%, rgba(198,161,91,1) 50%, rgba(198,161,91,0.85) 100%)",
          boxShadow: "0 0 16px rgba(198,161,91,0.55)",
        }}
      />

      {/* Handle */}
      <button
        type="button"
        aria-label="Drag to compare before and after"
        onKeyDown={onKeyDown}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        data-testid={`before-after-handle-${index}`}
        className="absolute top-1/2 flex items-center justify-center rounded-full transition-transform duration-150 focus:outline-none"
        style={{
          left: `${pct}%`,
          transform: "translate(-50%, -50%)",
          width: 52,
          height: 52,
          background: "rgba(250,247,242,0.14)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1.5px solid #C6A15B",
          color: "#C6A15B",
          cursor: "ew-resize",
          boxShadow: "0 8px 24px rgba(18,43,32,0.55)",
        }}
      >
        <ChevronLeft size={18} strokeWidth={2} style={{ marginRight: -4 }} />
        <ChevronRight size={18} strokeWidth={2} style={{ marginLeft: -4 }} />
      </button>
    </div>
  );
};

const BeforeAfter = () => {
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      id="before-after"
      className="relative"
      style={{
        background: "#F0EAE0",
        paddingTop: "clamp(4rem, 8vw, 7rem)",
        paddingBottom: "clamp(4rem, 8vw, 7rem)",
      }}
      data-testid="before-after-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <p className="eyebrow">The Transformation</p>
          <h2
            className="font-heading font-medium mt-4"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.4rem)",
              lineHeight: 1.1,
              color: "#122B20",
              letterSpacing: "-0.015em",
            }}
          >
            Drag the Slider.{" "}
            <span className="gold-italic">See What Changed.</span>
          </h2>
          <p
            className="mt-5 font-body text-[#2B2926]/85 max-w-xl"
            style={{ fontSize: "1.02rem", lineHeight: 1.7 }}
          >
            Same lot. Same street. New chapter. Slide across each image to see
            what a tired parcel becomes after Ibex sources it and a builder
            partner brings it back to life.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
        className="relative mt-12 md:mt-16"
      >
        <Swiper
          modules={[Pagination, Navigation, Autoplay]}
          slidesPerView={1}
          loop={BEFORE_AFTER_PAIRS.length > 1}
          speed={800}
          spaceBetween={0}
          allowTouchMove={false}
          autoplay={
            reduced || BEFORE_AFTER_PAIRS.length < 2
              ? false
              : { delay: 8000, disableOnInteraction: true, pauseOnMouseEnter: true }
          }
          pagination={{ clickable: true, el: ".ba-pagination" }}
          navigation={{ prevEl: ".ba-prev", nextEl: ".ba-next" }}
          className="w-full"
          data-testid="before-after-swiper"
        >
          {BEFORE_AFTER_PAIRS.map((p, i) => (
            <SwiperSlide key={i}>
              <CompareViewport
                before={p.before}
                after={p.after}
                label={p.label}
                index={i}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {BEFORE_AFTER_PAIRS.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous transformation"
              data-testid="ba-prev"
              className="ba-prev absolute -left-2 md:left-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
              style={{
                background: "rgba(250,247,242,0.14)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(198,161,91,0.55)",
                color: "#FAF7F2",
              }}
            >
              <ChevronLeft size={20} strokeWidth={1.6} />
            </button>
            <button
              type="button"
              aria-label="Next transformation"
              data-testid="ba-next"
              className="ba-next absolute -right-2 md:right-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
              style={{
                background: "rgba(250,247,242,0.14)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(198,161,91,0.55)",
                color: "#FAF7F2",
              }}
            >
              <ChevronRight size={20} strokeWidth={1.6} />
            </button>
          </>
        )}

        <div className="ba-pagination flex justify-center gap-2 mt-8" />
        <style>{`
          .ba-prev:hover, .ba-next:hover {
            background: rgba(198,161,91,0.30) !important;
            border-color: #C6A15B !important;
          }
          .ba-pagination .swiper-pagination-bullet {
            background: rgba(18,43,32,0.35) !important;
          }
          .ba-pagination .swiper-pagination-bullet-active {
            background: #C6A15B !important;
          }
        `}</style>
      </motion.div>
    </section>
  );
};

export default BeforeAfter;
